/** Debug settings scene — toggles all DebugConfig flags grouped by category, with a master on/off switch. */
import Phaser from 'phaser';
import { DebugConfig, DebugFlagKey, FlagCategory } from '../../debug/DebugConfig';
import { crispText, crispButton, setupCrispCamera } from '../../ui/CrispUtil';
import { Font, Hex, TextStyle, ButtonColor } from '../../ui/Theme';
import { GAME_WIDTH } from '../../utils/Constants';

interface ToggleRow {
  flag: DebugFlagKey;
  category: FlagCategory;
  labelText: Phaser.GameObjects.Text;
  valueText: Phaser.GameObjects.Text;
}

// Left column: overlays. Right column: gameplay + logging.
const LEFT_SECTIONS: { category: FlagCategory; title: string }[] = [
  { category: 'overlay', title: 'OVERLAYS' },
];
const RIGHT_SECTIONS: { category: FlagCategory; title: string }[] = [
  { category: 'gameplay', title: 'GAMEPLAY' },
  { category: 'logging', title: 'LOGGING' },
  { category: 'ui', title: 'UI' },
];

/**
 * Renders overlay, gameplay, and logging flags in two columns. Dimmed values indicate flags that
 * are raw-on but effectively off due to master being disabled. Provides quick-links to spawn,
 * despawn, and XP-grant sub-scenes.
 */
export class DebugSettingsScene extends Phaser.Scene {
  private rows: ToggleRow[] = [];
  private masterText!: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'DebugSettingsScene' });
  }

  create(): void {
    this.rows = [];
    setupCrispCamera(this);
    const width = GAME_WIDTH;

    this.cameras.main.setBackgroundColor(Hex.sceneBg);

    crispText(this, width / 2, 30, 'DEBUG SETTINGS', TextStyle.debugTitle).setOrigin(0.5);

    // Master toggle — centered
    const masterY = 68;
    crispText(this, width / 2 - 20, masterY, 'MASTER', {
      fontFamily: Font.family, fontSize: Font.sizeMaster, color: Hex.debugDefault,
    }).setOrigin(1, 0.5);

    const masterOn = DebugConfig.enabled;
    this.masterText = crispText(this, width / 2 + 20, masterY, masterOn ? 'ON' : 'OFF', {
      fontFamily: Font.family, fontSize: Font.sizeMaster, color: masterOn ? Hex.greenBright : Hex.redDefault,
    }).setOrigin(0, 0.5).setInteractive({ useHandCursor: true });

    this.masterText.on('pointerdown', () => {
      DebugConfig.setEnabled(!DebugConfig.enabled);
      this.refreshMaster();
      this.refreshAll();
    });

    // Two-column layout
    const colStartY = 92;
    const lineHeight = 18;
    const colGap = 20;
    const colWidth = (width - colGap) / 2;
    const leftLabelX = colWidth / 2 + 10;
    const leftValueX = leftLabelX + 20;
    const rightLabelX = colWidth + colGap + colWidth / 2 - 30;
    const rightValueX = rightLabelX + 20;

    const leftMaxY = this.buildColumn(LEFT_SECTIONS, leftLabelX, leftValueX, colStartY, lineHeight);
    const rightMaxY = this.buildColumn(RIGHT_SECTIONS, rightLabelX, rightValueX, colStartY, lineHeight);

    // Buttons below both columns
    const btnY = Math.max(leftMaxY, rightMaxY) + 20;
    const btnSpacing = 85;

    const buttons = [
      { label: 'SPAWN', color: ButtonColor.spawn, fn: () => this.scene.start('DebugSpawnScene') },
      { label: 'DESPAWN', color: ButtonColor.despawn, fn: () => this.scene.start('DebugDespawnScene') },
      { label: 'GRANT XP', color: ButtonColor.attune, fn: () => this.scene.start('DebugGrantXPScene') },
      { label: 'SET HP', color: ButtonColor.danger, fn: () => this.scene.start('DebugSetHPScene') },
      { label: 'RESET ALL', color: ButtonColor.warning, fn: () => { DebugConfig.resetAll(); this.refreshMaster(); this.refreshAll(); } },
    ];
    const totalWidth = (buttons.length - 1) * btnSpacing;
    const startX = width / 2 - totalWidth / 2;
    for (let i = 0; i < buttons.length; i++) {
      crispButton(this, startX + i * btnSpacing, btnY, buttons[i].label,
        Font.sizeSmall, buttons[i].color, buttons[i].fn);
    }

    this.input.keyboard!.on('keydown-ESC', () => this.goBack());
  }

  private buildColumn(
    sections: { category: FlagCategory; title: string }[],
    labelX: number, valueX: number,
    startY: number, lineHeight: number
  ): number {
    let y = startY;

    for (const section of sections) {
      y += 4;
      const headerCenterX = labelX + (valueX - labelX) / 2;
      crispText(this, headerCenterX, y, `— ${section.title} —`, TextStyle.section).setOrigin(0.5);

      // ALL / NONE toggle buttons next to section header
      const allBtn = crispText(this, headerCenterX + 65, y, 'ALL', {
        fontFamily: Font.family, fontSize: Font.sizeSmall, color: Hex.greenBright,
      }).setOrigin(0, 0.5).setInteractive({ useHandCursor: true });
      allBtn.on('pointerdown', () => {
        DebugConfig.setCategoryAll(section.category, true);
        this.refreshAll();
      });

      const noneBtn = crispText(this, headerCenterX + 105, y, 'NONE', {
        fontFamily: Font.family, fontSize: Font.sizeSmall, color: Hex.redDefault,
      }).setOrigin(0, 0.5).setInteractive({ useHandCursor: true });
      noneBtn.on('pointerdown', () => {
        DebugConfig.setCategoryAll(section.category, false);
        this.refreshAll();
      });

      y += 14;

      const flags = DebugConfig.flagsByCategory(section.category);

      for (const flag of flags) {
        const labelText = crispText(this, labelX, y, DebugConfig.flagLabel(flag), TextStyle.small)
          .setOrigin(1, 0.5);

        const rawOn = DebugConfig.rawFlags[flag];
        const effectiveOn = DebugConfig.flags[flag];
        const valueText = crispText(this, valueX, y, rawOn ? 'ON' : 'OFF', {
          fontFamily: Font.family, fontSize: Font.sizeSmall,
          color: effectiveOn ? Hex.greenBright : Hex.hpBorder,
        }).setOrigin(0, 0.5).setInteractive({ useHandCursor: true });

        valueText.on('pointerover', () => {
          if (DebugConfig.enabled) {
            valueText.setColor(DebugConfig.rawFlags[flag] ? '#44ff44' : Hex.textSecondary);
          }
        });
        valueText.on('pointerout', () => this.refreshRow(flag));
        valueText.on('pointerdown', () => {
          DebugConfig.toggle(flag);
          this.refreshRow(flag);
        });

        this.rows.push({ flag, category: section.category, labelText, valueText });
        y += lineHeight;
      }
    }

    return y;
  }

  private refreshMaster(): void {
    const on = DebugConfig.enabled;
    this.masterText.setText(on ? 'ON' : 'OFF');
    this.masterText.setColor(on ? Hex.greenBright : Hex.redDefault);
  }

  private refreshRow(flag: DebugFlagKey): void {
    const row = this.rows.find(r => r.flag === flag);
    if (!row) return;

    const rawOn = DebugConfig.rawFlags[flag];
    const masterOff = !DebugConfig.enabled;

    row.valueText.setText(rawOn ? 'ON' : 'OFF');

    if (masterOff) {
      row.valueText.setColor(rawOn ? '#336633' : Hex.textDim);
      row.labelText.setColor(Hex.textDisabled);
    } else {
      row.valueText.setColor(rawOn ? Hex.greenBright : Hex.hpBorder);
      row.labelText.setColor(Hex.textPrimary);
    }
  }

  private refreshAll(): void {
    for (const row of this.rows) {
      this.refreshRow(row.flag);
    }
  }

  private goBack(): void {
    this.scene.start('PauseScene');
  }
}
